// ─────────────────────────────────────────────────────────────────────────────
// Router único de la app: rutas de Pulso (antes estado local en App.tsx, ver PulsoLayout) + rutas
// de Reconocimiento (/app/…, sin cambios de contenido). Antes gobernaba solo /app/…; ver
// docs/superpowers/specs/2026-08-04-menu-unificado-design.md para el porqué de la fusión.
// ─────────────────────────────────────────────────────────────────────────────

import type { ReactElement } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { BASE_PATH } from '@/utils/basePath';
import { AppLayout } from './AppLayout';
import { PulsoLayout } from './PulsoLayout';
import { PULSO_VIEW_ROUTES } from './pulsoRoutes';
import { ReconocerView } from '@/views/recognition/ReconocerView';
import { BrillamosView } from '@/views/recognition/BrillamosView';
import { PerfilView } from '@/views/recognition/PerfilView';
import { InnovacionView } from '@/views/recognition/InnovacionView';
import { NominarView } from '@/views/recognition/NominarView';
import { AdminView as RecognitionAdminView } from '@/views/recognition/admin/AdminView';
import { RecognitionAnalyticsView } from '@/views/recognition/analytics/RecognitionAnalyticsView';
import { TeamView } from '@/views/recognition/metrics/TeamView';
import { TableroView } from '@/views/recognition/metrics/TableroView';
import { FrontlineRegisterView } from '@/views/recognition/access/FrontlineRegisterView';
import { FrontlineActivateView } from '@/views/recognition/access/FrontlineActivateView';
import { FrontlineResetPasswordView } from '@/views/recognition/access/FrontlineResetPasswordView';
import { LandingDecision } from '@/views/recognition/access/LandingDecision';
import { LoginView } from '@/views/LoginView';
import { FundamentalsView } from '@/views/FundamentalsView';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { GradientBackground } from '@/components/layout/GradientBackground';
import { PageSpinner } from '@/components/common';
import { SessionExpiredBanner } from '@/components/common/SessionExpiredBanner/SessionExpiredBanner';
import { useCurrentUser } from '@/hooks/useCurrentUser';

/**
 * Alguien se autenticó por AD (guest o interno) pero todavía no tiene ficha en Mongo
 * (`profileStatus === 'not-found'`, ver useCurrentUser). Solo puede ver el catálogo informativo
 * de Fundamentales — nada de Pulso ni Brillamos, en ninguna URL, hasta que el sync lo alcance o
 * un admin lo dé de alta. Reutiliza .view (misma clase que PulsoLayout); usa
 * .main-content--no-sidebar (no .main-content) porque este gate no renderiza ningún <Sidebar>
 * hermano que justifique el margin-left (hallazgo I2 de la revisión final, 2026-08-13).
 */
function UnregisteredProfileGate() {
  return (
    <>
      <GradientBackground />
      <main className="main-content--no-sidebar">
        <div className="view">
          <ErrorBoundary><FundamentalsView /></ErrorBoundary>
        </div>
      </main>
    </>
  );
}

export function AppRouter() {
  const currentUser = useCurrentUser();
  // REQ 3 — quien tiene fecha de ingreso futura se gatea EN EL MISMO SITIO. Se reutiliza este gate
  // en vez de inventar otro por dos razones: el efecto que pide el requisito es identico -- nada de
  // Pulso ni Brillamos, en ninguna URL -- y una pantalla propia necesitaria un texto nuevo en los
  // SIETE idiomas (la prueba de paridad i18n lo exige), o sea deuda a cambio de nada.
  //
  // Queda pendiente, y es una MEJORA y no el requisito: un mensaje propio que diga "tu acceso empieza
  // el <fecha>". Hoy no se puede sin decidir antes si se revela la fecha -- el backend devuelve a
  // proposito la decision y no el dato, porque `resolve` es anonimo.
  const isUnregistered = currentUser?.profileStatus === 'not-found'
    || currentUser?.profileStatus === 'pending-hire-date';
  // Sin esto, el árbol protegido (en particular AppLayout, que a diferencia de PulsoLayout no
  // espera profileResolved) se monta durante la ventana de carga — antes de saber si hay que
  // gatear. Bloquear aquí, no solo ante 'not-found' confirmado (hallazgo C1 de la revisión final,
  // 2026-08-13).
  const isLoadingProfile = !currentUser || currentUser.profileStatus === 'pending';

  // sonarjs/no-nested-conditional: las tres ramas son mutuamente excluyentes y cada una decide una
  // franja completa del árbol de rutas, así que se resuelven con if/else en vez de encadenar
  // ternarios en el JSX.
  let gatedRoutes: ReactElement;
  if (isUnregistered) {
    // Sin ficha en Mongo: cualquier otra URL (incluida /app, /fundamentales, /app/admin…) cae
    // aquí. No hay ruta de escape al armazón normal mientras profileStatus sea 'not-found' — ver
    // docs/superpowers/specs/2026-08-13-guest-user-ad-sync-design.md.
    gatedRoutes = <Route path="*" element={<UnregisteredProfileGate />} />;
  } else if (isLoadingProfile) {
    // Identidad aún no resuelta (currentUser null) o perfil todavía en 'pending': nunca montar el
    // árbol protegido en esta ventana — AppLayout no tiene guarda propia (hallazgo C1 de la
    // revisión final, 2026-08-13).
    gatedRoutes = <Route path="*" element={<PageSpinner />} />;
  } else {
    gatedRoutes = (
      <>
        {/* Regla #3 — Índice del Home: decide Reconocimiento vs Brillamos vs landing común.
            Va ANTES del comodín /app/* y fuera del armazón (resuelve y redirige/renderiza). */}
        <Route path="/app" element={<LandingDecision />} />

        <Route element={<PulsoLayout />}>
          {PULSO_VIEW_ROUTES.map(({ path, element }) => (
            <Route key={path} path={path} element={element} />
          ))}
        </Route>

        <Route element={<AppLayout />}>
          <Route path="/app/reconocer" element={<ReconocerView />} />
          <Route path="/app/brillamos" element={<BrillamosView />} />
          <Route path="/app/nominar" element={<NominarView />} />
          <Route path="/app/perfil" element={<PerfilView />} />
          <Route path="/app/innovacion" element={<InnovacionView />} />
          <Route path="/app/reportes" element={<RecognitionAnalyticsView />} />
          <Route path="/app/equipo" element={<TeamView />} />
          <Route path="/app/tablero" element={<TableroView />} />
          <Route path="/app/admin" element={<RecognitionAdminView />} />
          <Route path="/app/admin/:section" element={<RecognitionAdminView />} />
        </Route>

        {/* Dentro de la zona del módulo, cualquier ruta desconocida va a su portada — MISMA decisión
            que la de `/app` exacto (bug Sesión #98): antes iba siempre a /app/reconocer sin condición,
            así que un acceso "raro" (URL mal escrita, enlace viejo) no respetaba la regla que ya
            aplica el resto de la app. Reusar `LandingDecision` en vez de un destino fijo evita que
            las dos rutas vuelvan a divergir. */}
        <Route path="/app/*" element={<LandingDecision />} />

        {/* Fuera de /app, cualquier ruta desconocida vuelve al home de Pulso. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </>
    );
  }

  return (
    /* `basename` = el prefijo del ambiente (ver src/utils/basePath.ts). Vale '' en QA y PROD, así
       que ahí no cambia nada; en DEV vale '/dev' y es lo que hace que las rutas de abajo casen
       cuando la app se sirve bajo https://culture-qa.sigmafoods.com/dev.
       Sin él, react-router compara la URL COMPLETA contra rutas que empiezan en '/': ninguna casa,
       todo cae en el comodín `<Route path="*">` de más arriba y el usuario acaba en el portal de QA.
       Con él, las rutas y los `navigate('/app/...')` se siguen escribiendo SIN prefijo: react-router
       lo antepone y lo quita solo. */
    <BrowserRouter basename={BASE_PATH}>
      {/* Red de seguridad global (bug fila 100, UAT 2026-08-25): montado una sola vez, fuera de
          <Routes>, para que avise sin importar en qué pantalla ocurra. Ver SessionExpiredBanner. */}
      <SessionExpiredBanner />
      <Routes>
        <Route path="/login" element={<LoginView />} />

        {/* Alta de personal operativo sin cuenta previa: fuera del armazón a propósito. */}
        <Route path="/app/registro" element={<FrontlineRegisterView />} />

        {/* RF-002 — Activación del colaborador cargado por el padrón (fija contraseña desde el correo).
            Fuera del armazón y pública, igual que el registro: aún no hay sesión ni rol que gatear. */}
        <Route path="/app/activar" element={<FrontlineActivateView />} />

        {/* Restablecimiento de contraseña (enlace del correo). Fuera del armazón y pública, por lo
            mismo que las dos de arriba: quien llega aquí precisamente NO puede iniciar sesión. */}
        <Route path="/app/restablecer" element={<FrontlineResetPasswordView />} />

        {gatedRoutes}
      </Routes>
    </BrowserRouter>
  );
}

export default AppRouter;
